/**
 * The photograph this step of the auth flow stands on, wired to the browser.
 *
 * Every decision is `authHeroJourney`'s — the four coin tosses, what a stored record means, which
 * photograph a step draws under them. This is the two things that need a DOM: the session store,
 * and the network.
 *
 * `useState`'s lazy initialiser rather than an effect, for the reason the whole app uses it: an
 * effect would paint one photograph and swap it a frame later, which is the flash the loading
 * policy exists to prevent. The roll is decided before the first paint or it is not decided.
 *
 * ── Every step warms the NEXT one ────────────────────────────────────────────────────────────
 * The photograph changes on every step now, so every step is the last moment before a file is
 * needed. Asking for it when the step arrives means the pane holds its graded plate for however
 * long the file takes — right through the cross-fade that was supposed to show it. So the next
 * step's photograph is fetched on mount, while this step is still being read, and by the time the
 * trainer presses on it is in the browser's cache and the fade has something to fade to. Step 4
 * warms nothing: there is no fifth.
 *
 * `new Image()` rather than a `<link rel="preload">`: a link tag would have to be rendered into the
 * document head by a component that has no other reason to exist, and this is a fetch, not markup.
 * Nothing is done with the result — the cache entry IS the result.
 */

import { useEffect, useState } from 'react';

import {
  photoForStep,
  resolveAuthHeroRoll,
  type AuthHeroPhoto,
  type AuthHeroRoll,
  type AuthHeroStep,
} from './authHeroJourney';
import { sessionPreferenceStore } from './preferenceStore';

export function useAuthHeroPhoto(step: AuthHeroStep): AuthHeroPhoto {
  const [roll] = useState<AuthHeroRoll>(() =>
    resolveAuthHeroRoll(sessionPreferenceStore(), Math.random),
  );

  const nextSrc = step === 4 ? null : photoForStep(roll, (step + 1) as AuthHeroStep).src;
  useEffect(() => {
    if (nextSrc === null) return;
    new Image().src = nextSrc;
  }, [nextSrc]);

  return photoForStep(roll, step);
}
